import React, { lazy, useState, useEffect, useCallback, useRef, Suspense, } from 'react';
import { useRoutes, useLocation, Navigate, matchPath, useNavigate, useParams } from 'react-router-dom';
import publicRoutes from './publicRoutes';
import CRMpublicRoutes from './crm/publicRoutes';
import jsAPI from '../helpers/jsAPI';
import PrivateContext from '../context/PrivateContext';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import Slide from '@mui/material/Slide';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import qs from 'qs';
import Zoey from '../components/Zoey';
import RedAlertButton from '../components/RedAlertButton';
import common from '../helpers/common';
import appModuleList from '../app_modules';
import NotFound from '../screens/NotFound';
import PublicContext from '../context/PublicContext';
import useSessionSocket from '../hooks/useSessionSocket';

const AppLayout = lazy(() => import('../layouts/AppLayout'));
const emptyThreadNotificationState = {
  total_unread: 0,
  latest_item: null,
  items: [],
}

const canUseRoute = (accessModules, accessKey, isClient) => {
  const accessKeys = Array.isArray(accessKey) ? accessKey : [accessKey];
  return common.isAccessible(accessModules, accessKey) === true
    || accessKey === "always_show"
    || (isClient === true && accessKeys.includes("sc.threads"))
}

const ThreadNotificationSlideTransition = (props) => <Slide {...props} direction="up" />;

const RemoveTrailingSlash = () => {
  const routerLocation = useLocation();

  if (routerLocation.pathname.match('/.*/$')) {
    return (
      <Navigate
        replace
        to={{
          pathname: routerLocation.pathname.replace(/\/+$/, ''),
          search: routerLocation.search,
        }}
      />
    );
  }
  else {
    return null;
  }
}

const PrivateRoutes = React.memo((props) => {
  const { routes, } = props;

  return (
    useRoutes(routes)
  );
});

const PublicRoutes = React.memo(() => {
  const routes = [...publicRoutes, ...CRMpublicRoutes];

  return (
    useRoutes(routes)
  );
})

const AppRouter = () => {
  const routerLocation = useLocation();
  const routerNavigate = useNavigate();
  const firstRender = useRef(true);
  const stateReferenceCode = routerLocation.state?.referenceCode;
  const pathReferenceCode = routerLocation.pathname.startsWith("/crm/contract_agreement/")
    ? routerLocation.pathname.split("/")[3]
    : undefined;
  const referenceCode = stateReferenceCode || pathReferenceCode;
  const [isClient, setIsClient] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [users, setUsers] = useState({});
  const [s_id, setS_id] = useState(null);
  const [accessModules, setAccessModules] = useState(null);
  const [isImplementOnJSOnly, setIsImplementOnJSOnly] = useState(null);
  const [clientCode, setClientCode] = useState(null);
  const [currentAppModule, setCurrentAppModule] = useState([]);
  const [privateRoutes, setPrivateRoutes] = useState([]);
  const [appModules, setAppModules] = useState([]);
  const [isRoutesProcessed, setIsRoutesProcessed] = useState(false);
  const [sessionStatuses, setSessionStatuses] = useState([]);
  const [threadNotificationState, setThreadNotificationState] = useState(emptyThreadNotificationState);
  const [threadNotificationError, setThreadNotificationError] = useState('');
  // Zoey should mount only after the private session type is known.
  const shouldShowZoey = isClient === true || isClient === false;

  const checkAccess = useCallback(async () => {
    try {
      const responseJSON = await jsAPI.request("/api/modules/access");
      setAccessModules(responseJSON.data);
      setIsImplementOnJSOnly(responseJSON.isImplementOnJSOnly);
      setClientCode(responseJSON.clientCode);
      setSessionStatuses(responseJSON.statuses);
    }
    catch {
      setErrorMessage("Something went wrong. Please try to refresh your browser.");
    }
  }, []);

  const {
    sessionSocket,
    socketRef,
  } = useSessionSocket({
    s_id,
    accessModules,
    checkAccess,
  });

  const forceLogout = useCallback(async () => {
    try {
      const responseJSON = await jsAPI.request("/api/pub/logout");
      if (responseJSON.success === true) {
        if (sessionSocket) {
          sessionSocket.disconnect();
        }

        if (socketRef.current) {
          socketRef.current.disconnect();
          socketRef.current = null;
        }
      }
    }
    catch {}
    finally {
      setUsers({});
      setS_id(null);
      setAccessModules(null);
      setPrivateRoutes([]);
      setAppModules([]);
      setIsRoutesProcessed(true);
      routerNavigate("/login/sc", { replace: true });
    }
  }, [routerNavigate, sessionSocket, socketRef]);

  // useEffect(() => {
  //   const path = routerLocation.pathname;
  //   setBasePath(path.split("/")[1]?.toLowerCase());
  // }, [routerLocation.pathname]);

  const checkSession = useCallback(async () => {
    try {
      for(const status of sessionStatuses) {
        // Ensure we only redirect if not already prefixed with "/sc"
        if (routerLocation.pathname.startsWith(`/${status.code}_tickets`) && !routerLocation.pathname.startsWith(`/sc/${status.code}_tickets`)) {
          routerNavigate(`/sc${routerLocation.pathname}`, { replace: true });
        }
        else if (routerLocation.pathname.startsWith(`/client/${status.code}_tickets`) && !routerLocation.pathname.startsWith(`/sc/client${status.code}_tickets`)) {
          routerNavigate(`/sc${routerLocation.pathname}`, { replace: true });
        }
      }
      if (routerLocation.pathname.startsWith(`/sc/client/tickets`) && isClient === false) {
        const newPath = routerLocation.pathname.replace('/sc/client', '/sc');
        routerNavigate(newPath, { replace: true });
      }
      if (routerLocation.pathname.startsWith(`/sc/client/project_tickets`) && isClient === false) {
        const newPath = routerLocation.pathname.replace('/sc/client', '/sc');
        routerNavigate(newPath, { replace: true });
      }

      if (routerLocation.pathname.startsWith(`/sc/tickets`) && isClient === true) {
        const newPath = routerLocation.pathname.replace('/sc', '/sc/client');
        routerNavigate(newPath, { replace: true });
      }
      if (routerLocation.pathname.startsWith(`/sc/project_tickets`) && isClient === true) {
        const newPath = routerLocation.pathname.replace('/sc', '/sc/client');
        routerNavigate(newPath, { replace: true });
      }

      let redirectWhenHasSession = [...publicRoutes, ...CRMpublicRoutes].find((el) => el.path.toLowerCase() === routerLocation.pathname.toLowerCase())?.redirectWhenHasSession === true;
      if (firstRender.current === false && redirectWhenHasSession === false) return;
      let _basePath = routerLocation.pathname.split("/")[1];

      // if (!appModuleList.find((el) => el.basePath === "/" + _basePath) && redirectWhenHasSession === false) {
      //   setIsRoutesProcessed(true);
      //   return;
      // }
      const CRMpaths = CRMpublicRoutes.map(route => route.path);
      const filteredPaths = CRMpaths.filter(paths => paths !== "*").map(paths => paths.split(":")[0]);
      
      const SCpaths = publicRoutes.map(route => route.path);
      const filteredSCPaths = SCpaths.filter(paths => paths !== "*").map(paths => paths.split(":")[0]);

      const responseJSON = await jsAPI.request("/api/users/check_session");
      if (responseJSON.success === true) {
        if (responseJSON.users?.active === false) {
          await forceLogout();
          return;
        }

        setIsClient(responseJSON.is_client);
        setUsers({ ...responseJSON.users });
        setS_id(responseJSON.s_id);
        checkAccess();
        
  
        // Auto-redirect if client session exists and the URL is the root "/"
        if (responseJSON.is_client === true) {
          const createPasswordMatch = /^\/account\/create_password\/[^/]+$/.test(routerLocation.pathname);
          const forgotPasswordMatch = /^\/account\/forgot_password\/[^/]+$/.test(routerLocation.pathname);
          const isSupportHomePath = routerLocation.pathname.toLowerCase() === "/sc/home";
          
          if (routerLocation.pathname === "/" || routerLocation.pathname === "/sc/client/login") {
            routerNavigate(`/sc/client/home`, { replace: true });
            return;
          }
          if (isSupportHomePath) {
            routerNavigate(`/sc/client/home`, { replace: true });
            return;
          }
          if (!isSupportHomePath && !routerLocation.pathname.includes("/client/") && !filteredPaths.some(path => routerLocation.pathname.startsWith(path)) && !filteredSCPaths.some(path => routerLocation.pathname.startsWith(path))) {
            routerNavigate(`/sc/client${routerLocation.pathname.replace("/sc", "")}`, { replace: true });
            return;
          }
          if (filteredPaths.some(path => routerLocation.pathname.startsWith(path))) {  
            routerNavigate(`/crm${routerLocation.pathname.replace("/crm", "")}`, { replace: true });
            return;
          }
          if (routerLocation.pathname === "/account/create_password" || createPasswordMatch || routerLocation.pathname === "/account/forgot_password" || forgotPasswordMatch) {
            await forceLogout();
            return;
          }
        }

        if (redirectWhenHasSession === true) {
          const redirectPath = responseJSON.is_client === true ? "/sc/client/home" : "/app";
          window.location.href = redirectPath;
          return;
        }
      }
      else {
        if (responseJSON.inactive_session === true) {
          await forceLogout();
          return;
        }

        const queryString = qs.parse(routerLocation.search, { ignoreQueryPrefix: true });
        const { redirect_uri } = queryString;

        const leadSessionResponseJSON = await jsAPI.request('/api/leads/check_session?' + new URLSearchParams({ reference_code: referenceCode }));
        if(leadSessionResponseJSON.success === true) {
          setS_id(leadSessionResponseJSON.user_id);
          setAccessModules(leadSessionResponseJSON.modules);
        }

        if(routerLocation.pathname === "/crm/verify") {
          setIsRoutesProcessed(true);
          return;
        }
        
        if(routerLocation.pathname === "/crm/client_otp") {
          setIsRoutesProcessed(true);
          return;
        }
        
        if(routerLocation.pathname.startsWith(`/crm/contract_agreement`)) {
          setIsRoutesProcessed(true);
          return;
        }

        if (routerLocation.pathname === "/account/forgot_password") {
          setIsRoutesProcessed(true);
          return;
        }

        if (routerLocation.pathname === "/account/verify_otp") {
          setIsRoutesProcessed(true);
          return;
        }

        // Check if the path matches "/account/forgot_password/token"
        const forgotPasswordMatch = /^\/account\/forgot_password\/[^/]+$/.test(routerLocation.pathname);
        if (forgotPasswordMatch) {
            setIsRoutesProcessed(true);
            return;
        }

        if (routerLocation.pathname === "/account/create_password") {
          setIsRoutesProcessed(true);
          return;
        }

        // Check if the path matches "/account/create_password/token"
        const createPasswordMatch = /^\/account\/create_password\/[^/]+$/.test(routerLocation.pathname);
        if (createPasswordMatch ) {
            setIsRoutesProcessed(true);
            return;
        }

        if (!!redirect_uri === false && !!publicRoutes.find((el) => el.path === routerLocation.pathname.toLowerCase()) === false) {
          _basePath = _basePath || "";

          if (_basePath && !appModuleList.find((el) => el.basePath === "/" + _basePath && el.hasOwnLoginPage === true)) {
            _basePath = "";
          }

          const fullPathWithQuery = `${routerLocation.pathname}${routerLocation.search || ''}`;
          if (routerLocation.pathname.includes("app/logout")) {
            routerNavigate(`/login/${_basePath}`);
          }
          else {
            routerNavigate(`/login/${_basePath}?redirect_uri=${encodeURIComponent(fullPathWithQuery)}`);
          }
        }

        setIsRoutesProcessed(true);
      }
    }
    catch {
      setErrorMessage("Something went wrong. Please try to refresh your browser.");
    }
    finally {
      firstRender.current = false;
    }
  }, [routerNavigate, routerLocation.pathname, routerLocation.search, checkAccess, forceLogout, isClient, referenceCode, sessionStatuses]);

  const loadThreadNotifications = useCallback(async () => {
    if (isClient !== true || !accessModules) {
      setThreadNotificationState(emptyThreadNotificationState);
      return;
    }

    try {
      const responseJSON = await jsAPI.request('/api/threads/dashboard_highlight');
      if (responseJSON.success) {
        setThreadNotificationState(responseJSON.data || emptyThreadNotificationState);
      }
      else {
        setThreadNotificationState(emptyThreadNotificationState);
      }
    }
    catch {
      setThreadNotificationState(emptyThreadNotificationState);
    }
  }, [accessModules, isClient]);

  const getThreadNotificationPath = useCallback((item) => {
    if (!item?.entity_id) return '/sc/threads'
    if (item.is_announcement === true) {
      return {
        pathname: '/sc/threads',
        state: {
          highlightThreadId: item.thread_id || item.entity_id,
        },
      }
    }
    return item.entity_type === 'post'
      ? `/sc/threads/post/${item.entity_id}`
      : `/sc/threads/thread/${item.entity_id}`
  }, [])

  const markThreadNotificationAsSeen = useCallback(async (item) => {
    if (!item?.entity_type || !item?.entity_id) return

    const responseJSON = await jsAPI.request('/api/threads/dashboard_highlight/see', {
      method: 'POST',
      body: JSON.stringify({
        entity_type: item.entity_type,
        entity_id: item.entity_id,
      }),
    })

    if (!responseJSON.success) {
      throw new Error(responseJSON.error_message || 'Unable to mark the thread notification as seen.')
    }
  }, [])

  const handleSeeThreadNotification = useCallback(async () => {
    const latestItem = threadNotificationState.latest_item
    if (!latestItem) return

    try {
      await markThreadNotificationAsSeen(latestItem)
      await loadThreadNotifications()
      const nextPath = getThreadNotificationPath(latestItem)
      if (typeof nextPath === 'string') {
        routerNavigate(nextPath)
      }
      else {
        routerNavigate(nextPath.pathname, { state: nextPath.state })
      }
    }
    catch (err) {
      setThreadNotificationError(err.message)
    }
  }, [getThreadNotificationPath, loadThreadNotifications, markThreadNotificationAsSeen, routerNavigate, threadNotificationState.latest_item])

  const handleRemindThreadNotificationLater = useCallback(async () => {
    const latestItem = threadNotificationState.latest_item
    if (!latestItem) return

    try {
      const responseJSON = await jsAPI.request('/api/threads/dashboard_highlight/remind_later', {
        method: 'POST',
        body: JSON.stringify({
          entity_type: latestItem.entity_type,
          entity_id: latestItem.entity_id,
        }),
      })

      if (!responseJSON.success) {
        throw new Error(responseJSON.error_message || 'Unable to snooze the thread notification.')
      }

      await loadThreadNotifications()
    }
    catch (err) {
      setThreadNotificationError(err.message)
    }
  }, [loadThreadNotifications, threadNotificationState.latest_item])

  const handleOpenThreadNotificationHub = useCallback(() => {
    routerNavigate('/sc/threads')
  }, [routerNavigate])

  const handleSeeAllThreadNotifications = useCallback(async () => {
    try {
      const responseJSON = await jsAPI.request('/api/threads/dashboard_highlight/dismiss_all', {
        method: 'POST',
      })

      if (!responseJSON.success) {
        throw new Error(responseJSON.error_message || 'Unable to dismiss the thread notifications.')
      }

      setThreadNotificationState(emptyThreadNotificationState)
      handleOpenThreadNotificationHub()
    }
    catch (err) {
      setThreadNotificationError(err.message)
    }
  }, [handleOpenThreadNotificationHub])

  const handleRemindAllThreadNotificationsLater = useCallback(async () => {
    try {
      const responseJSON = await jsAPI.request('/api/threads/dashboard_highlight/remind_later_all', {
        method: 'POST',
      })

      if (!responseJSON.success) {
        throw new Error(responseJSON.error_message || 'Unable to snooze the thread notifications.')
      }

      await loadThreadNotifications()
    }
    catch (err) {
      setThreadNotificationError(err.message)
    }
  }, [loadThreadNotifications])

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  useEffect(() => {
    if (users?.id && accessModules) {
      loadThreadNotifications()
    }
  }, [accessModules, loadThreadNotifications, users?.id])

  useEffect(() => {
    const loadRoutes = async () => {
      try {
        const path = routerLocation.pathname;
        const basePath = (path.split("/")[1]?.toLowerCase());

        const { default: loadedModule } = await import(`./${basePath}/privateRoutes`);
        const { commonPrivateRoutes, clientPrivateRoutes, userPrivateRoutes } = loadedModule;
        let _routes = [];
        const currentApp = appModuleList.find((el) => el.basePath === `/${basePath}`);

        setCurrentAppModule({
          title: currentApp.name || "Jeonsoft ERP",
          logo: currentApp.logo || "/app_logo.png",
          basePath: currentApp.basePath,
          color: currentApp.color || '#EAF2FF',
        });

        for (const commonRoute of commonPrivateRoutes) {
          if (canUseRoute(accessModules, commonRoute.accessKey, isClient)) {
            _routes = [..._routes, { ...commonRoute }];
          }
        }

        if (isClient === true) {
          for (const clientRoute of clientPrivateRoutes) {
            if (canUseRoute(accessModules, clientRoute.accessKey, isClient)) {
              _routes = [..._routes, { ...clientRoute }];
            }
          }
        }
        else {
          for (const userRoute of userPrivateRoutes) {
            if (canUseRoute(accessModules, userRoute.accessKey, isClient)) {
              _routes = [..._routes, { ...userRoute }];
            }
          }
        }

        if (privateRoutes.length === 0 || !privateRoutes.some(route => matchPath({ path: route.path }, routerLocation.pathname))) {
          _routes = [..._routes, { path: "*", element: <NotFound /> }];
        } 

        setPrivateRoutes([..._routes]);

        // Major modules
        let _appModules = [];
        let _appModules2 = [];
        _appModules = isClient === true
          ? []
          : appModuleList.filter((el) => el.basePath !== `/${basePath}` && el.includeInList !== false);
        for (let _appModule of _appModules) {
          try {
            let hasAccess = false;
            hasAccess = accessModules.some((el) => el.code.split('.')[0] === _appModule.baseAccessKey);
            if (hasAccess === false) {
              // check for access_key that is "always_show"
              const { default: loadedModule } = await import(`../navigations/${_appModule.baseAccessKey}`);
              const navs = loadedModule();
              const userType = isClient === true ? "client" : "user";
              hasAccess = navs.filter((el) => ((el.userType === userType) || (el.userType === "both")))
                ?.some((el) => el.accessKey === "always_show");
            }

            if (hasAccess === true) {
              _appModules2 = [..._appModules2, {..._appModule}];
            }
          }
          catch {}
        }

        setAppModules([..._appModules2]);
      }
      catch (err) {
        setPrivateRoutes([]);
      }
      finally {
        setIsRoutesProcessed(true);
      }
    }

    if (!!users.id && !!accessModules) {
      loadRoutes();
    } 
  }, [routerLocation.pathname, accessModules, users.id, isClient]);

  useEffect(() => {
    if (!sessionSocket || isClient !== true) return

    const handler = () => {
      loadThreadNotifications()
    }

    sessionSocket.on('module-updated', handler)
    return () => sessionSocket.off('module-updated', handler)
  }, [isClient, loadThreadNotifications, sessionSocket])

  const latestThreadNotificationItem = threadNotificationState.latest_item || null
  const hasMultipleThreadNotifications = Number(threadNotificationState.total_unread || 0) >= 2
  const hasSingleThreadNotification = Number(threadNotificationState.total_unread || 0) === 1 && Boolean(latestThreadNotificationItem)
  const unreadThreadNotificationItems = threadNotificationState.items || []
  const unreadPostCount = unreadThreadNotificationItems.filter((item) => item.entity_type === 'post').length
  const unreadThreadCount = unreadThreadNotificationItems.filter((item) => item.entity_type === 'thread').length

  return (
    <>
      <RemoveTrailingSlash />
      <Suspense
        fallback={<LinearProgress />}
      >
        {isRoutesProcessed === false ? (
          <LinearProgress />
        ) : (
          <>
            {(() => {
              if (privateRoutes.filter(el => !!matchPath({ path: el.path.toLowerCase() }, routerLocation.pathname.toLowerCase())).length > 0) {
                return (
                  <PrivateContext.Provider
                    value={{
                      isClient,
                      users,
                      sessionSocket,
                      accessModules,
                      clientCode,
                      isImplementOnJSOnly,
                      currentAppModule,
                      appModules,
                    }}
                  >
                    <AppLayout>
                      <Snackbar
                        open={Boolean(threadNotificationError)}
                        autoHideDuration={5000}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        onClose={() => setThreadNotificationError('')}
                      >
                        <Alert severity="error" onClose={() => setThreadNotificationError('')}>
                          {threadNotificationError}
                        </Alert>
                      </Snackbar>

                      {Boolean(errorMessage) === true ? (
                        <Typography variant="h6">{errorMessage}</Typography>
                      ) : (
                        <PrivateRoutes
                          routes={privateRoutes}
                        />
                      )}

                      {isClient === true && (hasSingleThreadNotification || hasMultipleThreadNotifications) && latestThreadNotificationItem ? (
                        <Snackbar
                          open
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          TransitionComponent={ThreadNotificationSlideTransition}
                          sx={{
                            left: { xs: 12, sm: 48 },
                            bottom: { xs: 16, sm: 32 },
                            '& .MuiSnackbarContent-root': {
                              p: 0,
                              background: 'transparent',
                              boxShadow: 'none',
                            },
                          }}
                        >
                          <Card
                            sx={{
                              width: { xs: 'calc(100vw - 24px)', sm: 390 },
                              maxWidth: 'calc(100vw - 24px)',
                              borderRadius: 3,
                              border: '1px solid #d7e3fb',
                              boxShadow: '0 18px 46px rgba(36,59,143,0.18)',
                              overflow: 'hidden',
                            }}
                          >
                            <CardContent sx={{ p: 2 }}>
                              <Stack spacing={1.75} sx={{ minWidth: 0 }}>
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                                  <Box
                                    sx={{
                                      width: 56,
                                      height: 56,
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      background: 'linear-gradient(135deg, rgba(49,87,213,0.14) 0%, rgba(36,59,143,0.08) 100%)',
                                      border: '1px solid rgba(49,87,213,0.14)',
                                      flexShrink: 0,
                                      overflow: 'hidden',
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src="/zoey_remove_bg.png"
                                      alt="Zoey AI"
                                      sx={{
                                        width: 48,
                                        height: 48,
                                        objectFit: 'contain',
                                      }}
                                    />
                                  </Box>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        px: 1,
                                        py: 0.35,
                                        mb: 0.8,
                                        borderRadius: 999,
                                        color: '#3157d5',
                                        backgroundColor: 'rgba(49,87,213,0.08)',
                                        fontWeight: 800,
                                        letterSpacing: 0,
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      Zoey AI has notified you
                                    </Typography>
                                    <Typography sx={{ fontWeight: 900, color: '#14336c', lineHeight: 1.25, wordBreak: 'break-word' }}>
                                      {hasMultipleThreadNotifications
                                        ? unreadPostCount > 0 && unreadThreadCount === 0
                                          ? `${threadNotificationState.total_unread} new post updates are ready`
                                          : unreadThreadCount > 0 && unreadPostCount === 0
                                            ? `${threadNotificationState.total_unread} new thread updates are ready`
                                            : `${threadNotificationState.total_unread} new thread and post updates are ready`
                                        : latestThreadNotificationItem.entity_type === 'post'
                                          ? 'There is a new post'
                                          : latestThreadNotificationItem.is_announcement === true
                                            ? 'There is a new announcement'
                                            : 'There is a new thread'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, wordBreak: 'break-word' }}>
                                      {hasMultipleThreadNotifications
                                        ? unreadPostCount > 0 && unreadThreadCount === 0
                                          ? 'Open the thread module to review the latest post updates.'
                                          : unreadThreadCount > 0 && unreadPostCount === 0
                                            ? 'Open the thread module to review the latest thread updates.'
                                            : 'Open the thread module to review the latest thread and post updates.'
                                        : latestThreadNotificationItem.title}
                                    </Typography>
                                    {!hasMultipleThreadNotifications && latestThreadNotificationItem.thread_title && latestThreadNotificationItem.entity_type === 'post' ? (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                                        Thread: {latestThreadNotificationItem.thread_title}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                </Stack>

                                <Stack
                                  direction={{ xs: 'column', sm: 'row' }}
                                  spacing={1}
                                  justifyContent="flex-end"
                                  alignItems={{ xs: 'stretch', sm: 'center' }}
                                  sx={{ alignSelf: 'flex-end', pl: { sm: 8.75 } }}
                                >
                                  <Button
                                    variant="contained"
                                    onClick={hasMultipleThreadNotifications ? handleSeeAllThreadNotifications : handleSeeThreadNotification}
                                    sx={{ whiteSpace: 'nowrap', minWidth: 84 }}
                                  >
                                    View Now
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    onClick={hasMultipleThreadNotifications ? handleRemindAllThreadNotificationsLater : handleRemindThreadNotificationLater}
                                    sx={{ whiteSpace: 'nowrap' }}
                                  >
                                    Remind Me Later
                                  </Button>
                                </Stack>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Snackbar>
                      ) : null}

                      {shouldShowZoey && (
                        <>
                          <Zoey />
                          <RedAlertButton />
                        </>
                      )}
                    </AppLayout>
                  </PrivateContext.Provider>
                );
              }
              else if (privateRoutes.length === 0) {
                return (<PublicContext.Provider
                value={{sessionSocket}}
                >
                  <PublicRoutes />
                </PublicContext.Provider>);
              }
            })()}
          </>
        )}
      </Suspense>
    </>
  );
}

export default AppRouter;
